import api from '@/api';
import { IMdResident } from '@/types/api/medical/getResidents';
import { IResidentDFreeGraph } from '@/types/residents/dfreeGraph';
import dayjs from 'dayjs';
import React from 'react';
import { useParams } from 'react-router-dom';

// 入居者一覧（DFreeグラフページ）
export const useFetchDFreeGraphList = () => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const [formattedResidents, setFormattedResidents] = React.useState<(IResidentDFreeGraph | IMdResident)[]>([]);

  // 全ての施設向けサービス（serviceType=full)
  const fetchDFreeGrapListData = React.useCallback(
    async (
      nursingHomeUnitId: number | null = null,
      filterCondition: 'deviceAssigned' | 'deviceAttached' | null,
      start: string,
      end: string
    ) => {
      const residentsReq = api
        .get('/traversal/nursing-homes/:nursingHomeId', {
          params: nursingHomeUnitId ? { nursingHomeId, nursingHomeUnitId } : { nursingHomeId },
        })
        .catch((err) => {
          // 入居者が一人もいないunitを指定した場合にAPIから404が返ってくるので、入居者がいないレスポンスとして返す
          if (err?.response?.status === 404) {
            return { residents: [] };
          }
          throw err;
        });

      const reactionsReq = api.get('/traversal/nursing-homes/:nursingHomeId/sensor-reactions', {
        params: { nursingHomeId, from: start, to: end, sort: 'asc' },
      });

      const recordsReq = api.get('/traversal/nursing-homes/:nursingHomeId/care-records', {
        params: { nursingHomeId, from: start, to: end, limit: 10000 },
      });

      // activityStatusはstartと同時刻のレコードを取得するために1秒前を指定している
      const activityStatusReq = api
        .get('/traversal/nursing-homes/:nursingHomeId/activity-status-analysis', {
          params: { nursingHomeId, from: dayjs(start).subtract(1, 'second').utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]'), to: end },
        })
        .catch((err) => {
          if (err?.response?.status === 404) {
            return { nursingHomeId: +nursingHomeId, residents: [] };
          }
          throw err;
        });

      const [residentList, reactions, records, activityStatuses] = await Promise.all([
        residentsReq,
        reactionsReq,
        recordsReq,
        activityStatusReq,
      ]);

      const filteredResidents = (() => {
        if (filterCondition === 'deviceAssigned') {
          return residentList.residents.filter(
            (resident) => resident.serialNumber !== null || resident.gasSerialNumber !== null
          );
        } else if (filterCondition === 'deviceAttached') {
          return residentList.residents.filter(
            (resident) => resident.urineSize !== null || resident.gasPercentage !== null
          );
        } else {
          return residentList.residents;
        }
      })();

      let residents = filteredResidents.map((resident) => {
        const sensor = reactions.residents.find((v) => v.residentId === resident.id);
        const activityStatus = activityStatuses?.residents?.find((v) => v.residentId === resident.id);
        if (!sensor) {
          return {
            ...resident,
            rawSensorReactions: [],
            activityStatusAnalysis: activityStatus?.activityStatusAnalysis ?? [],
          };
        }
        const destSensorReactions: [number, number | null][] = [];
        const destRawSensorReactions: [number, number | null][] = [];
        let prevTimestamp = 0;

        sensor.sensorReactions.forEach((item) => {
          // 前後から25分以上離れていたら切断
          const timestamp = Date.parse(item.timestamp);
          if (timestamp - prevTimestamp > 60 * 1000 * 25) {
            destSensorReactions.push([timestamp - 60 * 1000, null]);
            destRawSensorReactions.push([timestamp - 60 * 1000, null]);
          }
          prevTimestamp = timestamp;

          destSensorReactions.push([timestamp, item.u1xActiveRatio * 10]);
          destRawSensorReactions.push([timestamp, item.activeRatio * 10]);
        });

        // care recored plots. want refactor.
        const careResidents = records?.residents;
        const careRecords = careResidents.find((v) => v.residentId === resident.id);
        if (!careRecords || Object.keys(careRecords.allCareRecords).length == 0) {
          return {
            ...resident,
            sensorReactions: destSensorReactions,
            rawSensorReactions: destRawSensorReactions,
            sensorNotifications: sensor.notifications,
            careRecordPlots: [],
            activityStatusAnalysis: activityStatus?.activityStatusAnalysis ?? [],
          };
        }
        const careRecordPlots = careRecords.allCareRecords.map((row) => {
          const v = row;
          return {
            timestamp: v.timestamp,
            toilet: v.urinatedToilet,
            pad: v.padUrineVolumeType !== 'none' && !!v.padUrineVolumeType,
          };
        });

        return {
          ...resident,
          sensorReactions: destSensorReactions,
          rawSensorReactions: destRawSensorReactions,
          sensorNotifications: sensor.notifications,
          careRecordPlots: careRecordPlots,
          activityStatusAnalysis: activityStatus?.activityStatusAnalysis ?? [],
        };
      });

      setFormattedResidents(residents);
      return residents;
    },
    [nursingHomeId]
  );

  // 医療向けサービスのみ（serviceType=medical）
  const fetchMdDFreeGrapListData = React.useCallback(
    async (
      nursingHomeUnitId: number | null = null,
      filterCondition: 'deviceAssigned' | 'deviceAttached' | null,
      start: string,
      end: string
    ) => {
      const residentsReq = api
        .get('/traversal/md/nursing-homes/:nursingHomeId/residents', {
          params: nursingHomeUnitId ? { nursingHomeId, nursingHomeUnitId } : { nursingHomeId },
        })
        .catch((err) => {
          // 入居者が一人もいないunitを指定した場合にAPIから404が返ってくるので、入居者がいないレスポンスとして返す
          if (err?.response?.status === 404) {
            return { residents: [] };
          }
          throw err;
        });

      const reactionsReq = api.get('/traversal/md/nursing-homes/:nursingHomeId/visualized-urine-volume', {
        params: { nursingHomeId, from: start, to: end, sort: 'asc' },
      });

      const recordsReq = api.get('/traversal/nursing-homes/:nursingHomeId/care-records', {
        params: { nursingHomeId, from: start, to: end, limit: 10000 },
      });

      const [residentList, reactions, records] = await Promise.all([residentsReq, reactionsReq, recordsReq]);

      const filteredResidents = (() => {
        if (filterCondition === 'deviceAssigned') {
          return residentList.residents.filter((resident) => resident.serialNumber !== null);
        } else if (filterCondition === 'deviceAttached') {
          return residentList.residents.filter((resident) => resident.isDeviceActive);
        } else {
          return residentList.residents;
        }
      })();

      let residents = filteredResidents.map((resident) => {
        const sensor = reactions.residents.find((v) => v.residentId === resident.id);
        if (!sensor) return resident;
        const destSensorReactions: { values: [number, number | null][]; volumes: [number, number | null][] } = {
          values: [],
          volumes: [],
        };
        let prevTimestamp: number;

        sensor.visualizedUrineVolumes.forEach((item) => {
          // 前後から25分以上離れていたら切断
          const timestamp = Date.parse(item.timestamp);
          if (timestamp - prevTimestamp > 60 * 1000 * 25) {
            destSensorReactions.values.push([timestamp - 60 * 1000, null]);
            destSensorReactions.volumes.push([timestamp - 60 * 1000, null]);
          }
          prevTimestamp = timestamp;

          // value は API から 100段階で来るので、表示は 10で割って四捨五入
          destSensorReactions.values.push([timestamp, Math.min(10, Math.max(0, Math.round(item['value'] / 10)))]);
          destSensorReactions.volumes.push([timestamp, item['volume']]);
        });

        // care recored plots. want refactor.
        const careResidents = records?.residents;
        const careRecords = careResidents.find((v) => v.residentId === resident.id);
        if (!careRecords || Object.keys(careRecords.allCareRecords).length == 0) {
          return {
            ...resident,
            sensorReactions: destSensorReactions,
            sensorNotifications: sensor.notifications,
            careRecordPlots: [],
          };
        }
        const careRecordPlots = careRecords.allCareRecords.map((row) => {
          const v = row;
          return {
            timestamp: v.timestamp,
            toilet: v.urinatedToilet,
            pad: v.padUrineVolumeType !== 'none' && !!v.padUrineVolumeType,
          };
        });

        return {
          ...resident,
          sensorReactions: destSensorReactions,
          sensorNotifications: sensor.notifications,
          careRecordPlots: careRecordPlots,
        };
      });

      setFormattedResidents(residents);
      return residents;
    },
    [nursingHomeId]
  );

  return { formattedResidents, fetchDFreeGrapListData, fetchMdDFreeGrapListData };
};
